import api from "./axiosConfig";

console.log('📁 BOOKING API FILE LOADED! 📁');

// NEW 6-STEP BOOKING API FLOW WITH REAL GOOGLE MAPS
// 1. Quote Creation (/api/quote) → 2. Fleet Selection (with fleet_id) → 3. Passenger Registration → 4. Booking Confirmation → 5. Final Booking → 6. Payment

// Step 1: Create Quote
export const createQuote = async (quoteData) => {
  console.log('[BookingAPI] Step 1: Creating quote with /api/quote');
  console.log('[BookingAPI] Quote data:', JSON.stringify(quoteData, null, 2));
  
  try {
    const response = await api.post('/api/quote', quoteData);
    console.log('[BookingAPI] Raw quote response:', JSON.stringify(response.data, null, 2));
    
    if (response.data && response.data.status === 1) {
      console.log('✅ Step 1 SUCCESS: Quote created with IDs:', {
        quote_info_record_id: response.data.data.quote_info_record_id,
        maps_record_id: response.data.data.maps_record_id,
        fleet_record_with_rate_id: response.data.data.fleet_record_with_rate_id
      });
      console.log('📋 Available fleets:', response.data.data.fleets);
      console.log('📋 Quote info:', response.data.data.quote_info);
      console.log('📋 Maps data:', response.data.data.maps);
      console.log('🗺️ Google Maps Status:', response.data.data.maps_status);
      
      // Validate critical IDs are present
      if (!response.data.data.quote_info_record_id) {
        throw new Error('quote_info_record_id is required but was null/undefined from quote response');
      }
      if (!response.data.data.maps_record_id) {
        throw new Error('maps_record_id is required but was null/undefined from quote response');
      }
      if (!response.data.data.fleet_record_with_rate_id) {
        throw new Error('fleet_record_with_rate_id is required but was null/undefined from quote response');
      }
      
      return {
        status: 1,
        data: {
          quote_info_record_id: response.data.data.quote_info_record_id,
          maps_record_id: response.data.data.maps_record_id,
          fleet_record_with_rate_id: response.data.data.fleet_record_with_rate_id,
          fleets: response.data.data.fleets,
          quote_info: response.data.data.quote_info,
          maps: response.data.data.maps,
          maps_status: response.data.data.maps_status
        }
      };
    }
    
    throw new Error(response.data?.message || 'Quote creation failed');
  } catch (error) {
    console.log('[BookingAPI] Step 1 FAILED: Quote creation error:', error);
    throw error;
  }
};

// Step 2: Select Fleet (WITH fleet_id parameter)
export const selectFleet = async (quoteData, fleetId, correctPickupDate, correctPickupTime) => {
  console.log('🔥🔥🔥 SELECT FLEET FUNCTION CALLED! 🔥🔥🔥');
  console.log('[BookingAPI] Step 2: Selecting fleet with /api/quote/booking-info');
  console.log(`[BookingAPI] Using fleet_id: ${fleetId} for selection.`);

  const selectionData = {
    quote_info_record_id: quoteData.quote_info_record_id?.toString() || '',
    maps_record_id: quoteData.maps_record_id?.toString() || '',
    fleet_record_with_rate_id: quoteData.fleet_record_with_rate_id?.toString() || '',
    fleet_id: fleetId // ✅ Sending the selected fleet_id
  };
  
  // Validate required IDs
  if (!quoteData.quote_info_record_id) {
    throw new Error('quote_info_record_id is required but was null/undefined from quote response');
  }
  if (!quoteData.maps_record_id) {
    throw new Error('maps_record_id is required but was null/undefined from quote response');
  }
  if (!quoteData.fleet_record_with_rate_id) {
    throw new Error('fleet_record_with_rate_id is required but was null/undefined from quote response');
  }
  
  console.log('[BookingAPI] Fleet selection data:', JSON.stringify(selectionData, null, 2));
  console.log('[BookingAPI] Debugging quoteData.pick_date:', quoteData.pick_date); // Add this line
  console.log(`[BookingAPI] ✅ CONFIRMING: fleet_id ${fleetId} is being sent to /api/quote/booking-info`);
  
  try {
    const response = await api.post('/api/quote/booking-info', selectionData);
    
    if (response.data && response.data.status === 1) {
      console.log('✅ Step 2 SUCCESS: Fleet selected with vehicle ID:', response.data.data.selected_vehicle_record_id);
      
      // 🛠️ WORKAROUND: Fix datetime in selected_fleet data
      const selectedFleet = response.data.data.selected_fleet || {};
      
      // Use correct datetime passed as parameters - FORCE OVERRIDE
      selectedFleet.pick_date = correctPickupDate;
      selectedFleet.pick_time = correctPickupTime;
      
      console.log('[BookingAPI] 🛠️ FIXED selected_fleet datetime:');
      console.log('- Original pick_date:', response.data.data.selected_fleet?.pick_date);
      console.log('- Fixed pick_date:', correctPickupDate);
      console.log('- Original pick_time:', response.data.data.selected_fleet?.pick_time);
      console.log('- Fixed pick_time:', correctPickupTime);
      console.log('- 🚨 FORCED OVERRIDE of backend stale data!');
      
      return {
        status: 1,
        data: {
          selected_vehicle_record_id: response.data.data.selected_vehicle_record_id,
          quote_info_record_id: response.data.data.quote_info_record_id,
          maps_record_id: response.data.data.maps_record_id,
          selected_fleet: selectedFleet // Use corrected datetime
        }
      };
    }
    
    throw new Error(response.data?.message || 'Fleet selection failed');
  } catch (error) {
    console.error('[BookingAPI] Step 2 FAILED: Fleet selection error:', error);
    throw error;
  }
};

// Step 3: Confirm Booking
export const confirmBookingInfo = async (selectionData, bookingDetails) => {
  console.log('[BookingAPI] Step 3: Confirming booking with /api/quote/confirm');
  
  const confirmData = {
    quote_info_record_id: selectionData.quote_info_record_id.toString(),
    maps_record_id: selectionData.maps_record_id.toString(),
    fleet_record_with_rate_id: selectionData.fleet_record_with_rate_id?.toString() || '',
    selected_vehicle_record_id: selectionData.selected_vehicle_record_id.toString(),
    passenger_id: bookingDetails.passenger_id.toString(),
    pickup_datetime: bookingDetails.pickup_datetime,
    special_instruction: bookingDetails.special_instruction || '',
    flight_no: bookingDetails.flight_no || '',
    flight_name: bookingDetails.flight_name || '',
    phone: bookingDetails.phone || '',
    customer_first_name: bookingDetails.customer_first_name || '',
    customer_last_name: bookingDetails.customer_last_name || '',
    customer_email: bookingDetails.customer_email || '',
    is_someone_else: bookingDetails.is_someone_else ? 1 : 0,
    rider_name: bookingDetails.is_someone_else ? (bookingDetails.rider_name || '') : '',
    rider_cell_no: bookingDetails.is_someone_else ? (bookingDetails.rider_cell_no || '') : '',
    rider_email: bookingDetails.is_someone_else ? (bookingDetails.rider_email || '') : ''
  };

  // DEBUG: Log booking confirmation data
  console.log('[BookingAPI] Booking confirmation data being sent:');
  console.log('- customer_first_name:', bookingDetails.customer_first_name);
  console.log('- customer_last_name:', bookingDetails.customer_last_name);
  console.log('- customer_email:', bookingDetails.customer_email);
  console.log('- pickup_datetime:', bookingDetails.pickup_datetime);
  console.log('- Full confirmData:', JSON.stringify(confirmData, null, 2));

  console.log('[BookingAPI] Booking confirmation data:', JSON.stringify(confirmData, null, 2));
  
  try {
    const response = await api.post('/api/quote/confirm', confirmData);
    
    console.log('[BookingAPI] Raw /api/quote/confirm response:', JSON.stringify(response.data, null, 2));
    
    if (response.data && response.data.status === 1) {
      console.log('✅ Step 3 SUCCESS: Booking confirmed with booking_info_record_id:', response.data.data?.booking_info_record_id);
      console.log('Full response.data structure:', JSON.stringify(response.data.data, null, 2));
      
      return {
        status: 1,
        data: {
          booking_info_record_id: response.data.data?.booking_info_record_id,
          quote_info_record_id: response.data.data?.quote_info_record_id,
          selected_vehicle_record_id: response.data.data?.selected_vehicle_record_id,
          maps_record_id: response.data.data?.maps_record_id
        }
      };
    }
    
    throw new Error(response.data?.message || 'Booking confirmation failed');
  } catch (error) {
    console.error('[BookingAPI] Step 3 FAILED: Booking confirmation error:', error);
    throw error;
  }
};

// Step 4: Create Final Booking (WITH passenger_id parameter)
export const createFinalBooking = async (bookingData, passengerId) => {
  console.log('[BookingAPI] Step 4: Creating final booking with /api/booking/create');
  
  // Validate passenger_id is provided
  if (!passengerId) {
    throw new Error('passenger_id is required for final booking creation');
  }
  
  // Validate required IDs are present
  if (!bookingData.booking_info_record_id) {
    throw new Error('booking_info_record_id is required but was null/undefined from booking confirmation');
  }
  if (!bookingData.quote_info_record_id) {
    throw new Error('quote_info_record_id is required but was null/undefined from booking confirmation');
  }
  if (!bookingData.selected_vehicle_record_id) {
    throw new Error('selected_vehicle_record_id is required but was null/undefined from booking confirmation');
  }

  const finalBookingData = {
    booking_info_record_id: bookingData.booking_info_record_id.toString(),
    quote_info_record_id: bookingData.quote_info_record_id.toString(),
    selected_vehicle_record_id: bookingData.selected_vehicle_record_id.toString(),
    passenger_id: passengerId.toString()  // ✅ REQUIRED parameter
  };
  
  console.log('[BookingAPI] Final booking data:', JSON.stringify(finalBookingData, null, 2));
  
  try {
    const response = await api.post('/api/booking/create', finalBookingData);
    
    console.log('[BookingAPI] Raw /api/booking/create response:', JSON.stringify(response.data, null, 2));
    
    if (response.data && response.data.status === 1) {
      console.log('✅ Step 4 SUCCESS: Final booking created with ref ID:', response.data.data.booking_ref_id);
      
      return {
        status: 1,
        booking_ref_id: response.data.data.booking_ref_id,
        passenger_id: response.data.data.passenger_id,
        booking_id: response.data.data.booking_id
      };
    }
    
    throw new Error(response.data?.message || 'Final booking creation failed');
  } catch (error) {
    console.error('[BookingAPI] Step 4 FAILED: Final booking creation error:', error);
    
    // Log the actual error response from the server
    if (error.response && error.response.data) {
      console.error('[BookingAPI] Server error response:', JSON.stringify(error.response.data, null, 2));
      console.error('[BookingAPI] Server error status:', error.response.status);
    }
    
    throw error;
  }
};

// Complete 6-Step Booking Flow
export const createBookingWithPayment = async (bookingData) => {
  console.log('🚨🚨🚨 BOOKING API CREATE BOOKING WITH PAYMENT CALLED! 🚨🚨🚨');
  console.log('[BookingAPI] Starting NEW 6-STEP BOOKING FLOW WITH REAL GOOGLE MAPS');
  console.log('[BookingAPI] Input booking data:', JSON.stringify(bookingData, null, 2));
  
  const start = Date.now();
  
  try {
    // Step 1: Create fresh quote instead of using stale Redux data
    console.log('[BookingAPI] Creating fresh quote to avoid stale data issues');
    console.log('[BookingAPI] Raw bookingData.journey_date:', bookingData.journey_date);
    // Parse the journey date properly
    const journeyParts = bookingData.journey_date?.split(' ') || [];
    const pickupDate = journeyParts[0] || '09/05/2025';
    const pickupTime = journeyParts.slice(1).join(' ') || '1:00am';
    
    // Create consistent datetime format for both quote and confirmation
    const consistentPickupDatetime = `${pickupDate} ${pickupTime}`;
    
    console.log('[BookingAPI] Parsed datetime values:');
    console.log('- pickupDate:', pickupDate);
    console.log('- pickupTime:', pickupTime);
    console.log('- consistentPickupDatetime:', consistentPickupDatetime);
    
    const quoteData = {
      start: bookingData.start,
      start_lat: bookingData.pickup?.coordinates?.lat || 42.3656338,
      start_lng: bookingData.pickup?.coordinates?.lng || -71.0282381,
      end: bookingData.end,
      end_lat: bookingData.dropoff?.coordinates?.lat || 42.3656338,
      end_lng: bookingData.dropoff?.coordinates?.lng || -71.0282381,
      pick_date: pickupDate,
      pick_time: pickupTime,
      passengers: bookingData.passengers || bookingData.passenger || 1,
      luggage: bookingData.luggage || 1,
      service_type: bookingData.service_type || 'from_airport',
      domain_id: bookingData.domain_id,
      infant_seat: bookingData.infant_seat || bookingData.baby_seat_count || 0,
      regular_seat: bookingData.regular_seat || 0,
      booster_seat: bookingData.booster_seat || 0
    };
    
    const quoteResult = await createQuote(quoteData);
    
    // Step 2: Select Fleet (Now sending fleet_id)
    console.log(`[BookingAPI] About to call selectFleet with fleet_id: ${bookingData.fleet_id}`);
    const fleetResult = await selectFleet(quoteResult.data, bookingData.fleet_id, pickupDate, pickupTime);
    
    // Step 3: Confirm Booking Info
    const confirmResult = await confirmBookingInfo({
      quote_info_record_id: fleetResult.data.quote_info_record_id,
      maps_record_id: fleetResult.data.maps_record_id,
      fleet_record_with_rate_id: quoteResult.data.fleet_record_with_rate_id,
      selected_vehicle_record_id: fleetResult.data.selected_vehicle_record_id
    }, {
      passenger_id: bookingData.passenger_id,
      pickup_datetime: consistentPickupDatetime,
      special_instruction: bookingData.special_requirements || '',
      flight_no: bookingData.flight_number || '',
      flight_name: bookingData.flight_name || '',
      phone: bookingData.customer_cell_no || '',
      // Add customer name and email
      customer_first_name: bookingData.passenger_first_name || '',
      customer_last_name: bookingData.passenger_last_name || '',
      customer_email: bookingData.customer_email || '',
      is_someone_else: bookingData.is_someone_else ? 1 : 0,
      rider_name: bookingData.rider_name || '',
      rider_cell_no: bookingData.rider_cell_no || '',
      rider_email: bookingData.rider_email || ''
    });
    
    // Step 4: Create Final Booking (WITH passenger_id)
    // Use the fresh quote_info_record_id from Step 3 response instead of old Redux data
    const finalBookingData = {
      booking_info_record_id: confirmResult.data.booking_info_record_id,
      quote_info_record_id: confirmResult.data.quote_info_record_id, // Use fresh ID from Step 3
      selected_vehicle_record_id: confirmResult.data.selected_vehicle_record_id,
      maps_record_id: confirmResult.data.maps_record_id
    };
    
    const finalResult = await createFinalBooking(finalBookingData, bookingData.passenger_id);
    
    const end = Date.now();
    console.log(`✅ NEW 6-STEP FLOW COMPLETE! Total time: ${end - start}ms`);
    console.log('Final booking_ref_id:', finalResult.booking_ref_id);
    console.log('🗺️ Google Maps integration active with real distance/time calculations');
    
    return finalResult;
    
  } catch (error) {
    console.error('[BookingAPI] 6-STEP FLOW FAILED:', error.message);
    throw error;
  }
};




// Add 2-second delay for payment processing timing
export const addPaymentDelay = () => {
  return new Promise(resolve => setTimeout(resolve, 2000));
};

// Register passenger (Step 0 - if needed)
export const registerPassenger = async (passengerData) => {
  console.log('[BookingAPI] Step 0: Registering passenger with /quote_api/register_passenger');
  
  try {
    const response = await api.post('/quote_api/register_passenger', {
      customer_title: passengerData.customer_title || 'Mr',
      customer_first_name: passengerData.customer_first_name || '',
      customer_last_name: passengerData.customer_last_name || '',
      customer_email: passengerData.customer_email,
      customer_cell_no: passengerData.customer_cell_no
    });
    
    if (response.data && (response.data.status === 1 || response.data.passenger_id)) {
      console.log('✅ Step 0 SUCCESS: Passenger registered with ID:', response.data.passenger_id);
      
      return {
        status: 1,
        passenger_id: response.data.passenger_id,
        msg: response.data.msg || 'Passenger registered successfully'
      };
    }
    
    throw new Error(response.data?.msg || 'Passenger registration failed');
  } catch (error) {
    console.error('[BookingAPI] Step 0 FAILED: Passenger registration error:', error);
    throw error;
  }
};
